import React, { useEffect, useState, useMemo } from 'react';
import type {
  Machine,
  ChecklistTemplate,
  FrequencyType,
  ItemResult,
  InspectionRecord,
  OverallInspectionStatus,
  Inspector,
} from '../types';
import {
  db,
  getTemplateById,
  updateMachineHours,
  updateMachineStatus,
  addToSyncQueue,
  getSetting,
  getActiveInspectors,
} from '../db';
import { dispatchAnomalyAlert } from '../services/line';
import { TOS_VARNSDORF_WHNQ1315_TEMPLATE } from '../data/defaultTemplates';
import {
  MachineSelector,
  FrequencyTabs,
  ChecklistCard,
  SignaturePad,
  LubricantGuideModal,
} from '../components/inspection';
import {
  Send,
  CheckCircle2,
  AlertTriangle,
  Clock,
  User,
  RotateCcw,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';

interface InspectionViewProps {
  machines: Machine[];
  selectedMachineId: string;
  onSelectMachine: (machineId: string) => void;
  onInspectionSubmitted?: (record: InspectionRecord) => void;
}

export const InspectionView: React.FC<InspectionViewProps> = ({
  machines,
  selectedMachineId,
  onSelectMachine,
  onInspectionSubmitted,
}) => {
  const selectedMachine =
    machines.find((m) => m.id === selectedMachineId) || machines[0];

  const [activeFrequency, setActiveFrequency] = useState<FrequencyType>('DAILY');
  const [template, setTemplate] = useState<ChecklistTemplate>(TOS_VARNSDORF_WHNQ1315_TEMPLATE);
  const [isLubricantModalOpen, setIsLubricantModalOpen] = useState<boolean>(false);

  // Inspector & Session state
  const [inspectors, setInspectors] = useState<Inspector[]>([]);
  const [inspectorName, setInspectorName] = useState<string>('สมศักดิ์ มีชัย');
  const [shift, setShift] = useState<string>('กะเช้า (Day Shift)');
  const [runningHours, setRunningHours] = useState<string>(
    selectedMachine ? String(selectedMachine.cumulativeHours) : '1850.5'
  );
  const [supervisorNotes, setSupervisorNotes] = useState<string>('');
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | undefined>(undefined);

  // Results map keyed by itemId
  const [resultsMap, setResultsMap] = useState<Record<string, ItemResult>>({});

  // Submission UI state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [lastSubmittedRecord, setLastSubmittedRecord] = useState<InspectionRecord | null>(null);

  // Load active inspectors list
  useEffect(() => {
    let isMounted = true;
    const loadInspectors = async () => {
      try {
        const list = await getActiveInspectors();
        if (isMounted && list.length > 0) {
          setInspectors(list);
          // Default to first active inspector if current is not in list
          if (!list.some((i) => i.name === inspectorName)) {
            setInspectorName(list[0].name);
          }
        }
      } catch (err) {
        console.error('[InspectionView] Error loading inspectors:', err);
      }
    };
    loadInspectors();
    return () => {
      isMounted = false;
    };
  }, []);

  // Sync running hours when machine changes
  useEffect(() => {
    if (selectedMachine) {
      setRunningHours(String(selectedMachine.cumulativeHours));
    }
  }, [selectedMachine?.id, selectedMachine?.cumulativeHours]);

  // Load Template when machine or assignedTemplateId changes
  useEffect(() => {
    let isMounted = true;
    const loadTemplate = async () => {
      if (!selectedMachine) return;
      try {
        const tpl = await getTemplateById(selectedMachine.assignedTemplateId);
        if (isMounted) {
          if (tpl) {
            setTemplate(tpl);
          } else {
            // Fallback to default
            setTemplate(TOS_VARNSDORF_WHNQ1315_TEMPLATE);
          }
        }
      } catch (err) {
        console.error('[InspectionView] Error loading template:', err);
        if (isMounted) {
          setTemplate(TOS_VARNSDORF_WHNQ1315_TEMPLATE);
        }
      }
    };
    loadTemplate();
    return () => {
      isMounted = false;
    };
  }, [selectedMachine?.assignedTemplateId]);

  // Filter items according to active frequency
  const currentItems = useMemo(() => {
    if (!template || !template.items) return [];
    return template.items.filter((item) => item.frequency === activeFrequency);
  }, [template, activeFrequency]);

  // Item counts for frequency tabs
  const itemCounts = useMemo(() => {
    if (!template || !template.items) {
      return { DAILY: 9, WEEKLY: 3, MONTHLY: 5, HOURS: 9 };
    }
    return {
      DAILY: template.items.filter((i) => i.frequency === 'DAILY').length,
      WEEKLY: template.items.filter((i) => i.frequency === 'WEEKLY').length,
      MONTHLY: template.items.filter((i) => i.frequency === 'MONTHLY').length,
      HOURS: template.items.filter((i) => i.frequency === 'HOURS').length,
    };
  }, [template]);

  // Initialize or maintain result for each current item
  const updateItemResult = (newResult: ItemResult) => {
    setResultsMap((prev) => ({
      ...prev,
      [newResult.itemId]: newResult,
    }));
  };

  // Compile active results list for current frequency
  const activeResults: ItemResult[] = useMemo(() => {
    return currentItems.map((item) => {
      const existing = resultsMap[item.id];
      if (existing) return existing;

      // Default fallback result
      const isDoneType = item.inputType === 'done_checkbox';
      return {
        itemId: item.id,
        itemNo: item.itemNo,
        frequency: item.frequency,
        taskName: item.taskNameTh,
        criteria: item.criteriaTh,
        status: isDoneType ? 'DONE' : 'OK',
        isAbnormal: false,
      };
    });
  }, [currentItems, resultsMap]);

  // Check if any item in active results has NG or out-of-tolerance
  const hasDefects = useMemo(() => {
    return activeResults.some((res) => res.status === 'NG' || res.isAbnormal);
  }, [activeResults]);

  const defectCount = useMemo(() => {
    return activeResults.filter((res) => res.status === 'NG' || res.isAbnormal).length;
  }, [activeResults]);

  // Handle Form Submission
  const handleSubmitInspection = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedMachine) {
      setSubmitError('กรุณาเลือกเครื่องจักร');
      return;
    }

    if (!inspectorName.trim()) {
      setSubmitError('กรุณาระบุชื่อผู้ตรวจสอบ (Inspector Name is required)');
      return;
    }

    setSubmitError('');
    setIsSubmitting(true);

    try {
      const now = new Date().toISOString();
      const recordId = `rec_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      const numericHours = parseFloat(runningHours) || selectedMachine.cumulativeHours;
      const overallStatus: OverallInspectionStatus = hasDefects ? 'DEFECT_FOUND' : 'PASS';

      const inspectionRecord: InspectionRecord = {
        id: recordId,
        machineId: selectedMachine.id,
        machineName: selectedMachine.name,
        templateId: template.id,
        frequency: activeFrequency,
        inspectorName: inspectorName.trim(),
        inspectionDate: now,
        shift,
        operatingHours: numericHours,
        results: activeResults,
        overallStatus,
        signatureDataUrl,
        supervisorNotes: supervisorNotes.trim() || undefined,
        alertDispatched: false,
        syncStatus: 'PENDING_SYNC',
        createdAt: now,
        updatedAt: now,
      };

      // 1. Save inspection to IndexedDB inspections table
      await db.inspections.put(inspectionRecord);

      // 2. Enqueue to syncQueue table for Google Sheets / LINE dispatch
      const gasUrl = (await getSetting<string>('gas_endpoint_url')) || '';
      await addToSyncQueue({
        queueId: `queue_${recordId}_${Date.now()}`,
        recordId: recordId,
        endpointType: 'google_sheets',
        targetUrl: gasUrl,
        payload: {
          action: 'single_inspection',
          inspections: [inspectionRecord],
          timestamp: now,
        },
        attempts: 0,
        maxAttempts: 5,
        status: 'PENDING',
        createdAt: now,
      });

      // 3. Update machine cumulative hours and last inspection date
      await updateMachineHours(selectedMachine.id, numericHours);
      if (hasDefects) {
        await updateMachineStatus(selectedMachine.id, 'DUE_SOON');
        // Dispatch real-time anomaly alert via LINE / Webhook
        try {
          await dispatchAnomalyAlert(inspectionRecord);
        } catch (alertErr) {
          console.warn('[InspectionView] Alert dispatch error (safely handled):', alertErr);
        }
      } else {
        await updateMachineStatus(selectedMachine.id, 'NORMAL');
      }
      await db.machines.update(selectedMachine.id, {
        lastInspectionDate: now,
        lastInspectionHours: numericHours,
        updatedAt: now,
      });

      setLastSubmittedRecord(inspectionRecord);
      if (onInspectionSubmitted) {
        onInspectionSubmitted(inspectionRecord);
      }
    } catch (err: any) {
      console.error('[InspectionView] Error submitting inspection:', err);
      setSubmitError(err.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setLastSubmittedRecord(null);
    setResultsMap({});
    setSignatureDataUrl(undefined);
    setSupervisorNotes('');
  };

  return (
    <div className="space-y-6">
      {/* 1. Target Machine Selector */}
      <MachineSelector
        machines={machines}
        selectedMachineId={selectedMachineId}
        onSelectMachine={(id) => {
          onSelectMachine(id);
          resetForm();
        }}
      />

      {/* 2. Frequency Tabs Bar */}
      <FrequencyTabs
        activeFrequency={activeFrequency}
        onSelectFrequency={(freq) => {
          setActiveFrequency(freq);
          setLastSubmittedRecord(null);
        }}
        itemCounts={itemCounts}
        onOpenLubricantGuide={() => setIsLubricantModalOpen(true)}
      />

      {/* Submission Success Banner */}
      {lastSubmittedRecord && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-5 space-y-3 animate-in fade-in zoom-in-95 duration-200 shadow-xs">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2.5 text-emerald-800 font-bold text-base sm:text-lg">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <span>บันทึกผลการตรวจเช็ก PM สำเร็จแล้ว!</span>
            </div>
            <button
              type="button"
              onClick={resetForm}
              className="px-3.5 py-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-bold flex items-center gap-1.5 transition-colors border border-emerald-300"
            >
              <RotateCcw className="w-4 h-4" />
              ตรวจรอบใหม่
            </button>
          </div>

          <div className="text-xs text-emerald-900 space-y-1">
            <p>
              รหัสบันทึก:{' '}
              <span className="font-mono font-bold text-emerald-950">
                {lastSubmittedRecord.id}
              </span>{' '}
              | เครื่องจักร:{' '}
              <strong className="text-emerald-950">{lastSubmittedRecord.machineName}</strong>
            </p>
            <p>
              ผลสรุป:{' '}
              <span
                className={`font-bold px-2 py-0.5 rounded text-xs ${
                  lastSubmittedRecord.overallStatus === 'PASS'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-rose-600 text-white'
                }`}
              >
                {lastSubmittedRecord.overallStatus === 'PASS'
                  ? 'ปกติ (PASS)'
                  : 'พบข้อบกพร่อง (DEFECT FOUND)'}
              </span>{' '}
              | สถานะคิว:{' '}
              <span className="font-mono text-emerald-800 font-bold">
                PENDING_SYNC (พร้อมส่ง Google Sheets)
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Overall Health Status Bar for Current Inspection */}
      <div
        className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-all ${
          hasDefects
            ? 'bg-rose-50 border-rose-200 text-rose-800 shadow-xs'
            : 'bg-white border-slate-200 text-slate-800 shadow-xs'
        }`}
      >
        <div className="flex items-center gap-2.5">
          {hasDefects ? (
            <ShieldAlert className="w-6 h-6 text-rose-600 shrink-0 animate-bounce" />
          ) : (
            <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
          )}
          <div>
            <h4 className="font-bold text-sm sm:text-base text-slate-900">
              {hasDefects
                ? `พบข้อบกพร่อง / นอกเกณฑ์มาตรฐาน (${defectCount} รายการ)`
                : 'ผลการตรวจสอบทุกรายการผ่านเกณฑ์มาตรฐาน (Normal)'}
            </h4>
            <p className="text-xs text-slate-500">
              {hasDefects
                ? 'ระบบจะสร้างแจ้งเตือนส่งไปยังหัวหน้างานและ LINE Webhook อัตโนมัติ'
                : 'รายการตรวจเช็กพร้อมสำหรับการลงนามและบันทึกข้อมูล'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-mono px-2.5 py-1 rounded bg-slate-100 border border-slate-200 text-slate-700 font-semibold">
            {activeResults.length} รายการตรวจ
          </span>
        </div>
      </div>

      {/* 3. Inspection Item Cards List */}
      <div className="space-y-4">
        {currentItems.map((item) => {
          const currentRes = activeResults.find((r) => r.itemId === item.id);
          return (
            <ChecklistCard
              key={item.id}
              item={item}
              result={currentRes}
              onUpdateResult={updateItemResult}
              disabled={isSubmitting}
            />
          );
        })}
      </div>

      {/* 4. Inspector Session Info & Hours */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-4 shadow-xs">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2.5">
          <User className="w-4 h-4 text-blue-600" />
          ข้อมูลผู้บันทึกและกะการทำงาน (Inspector Information)
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label
              htmlFor="inspector-name-select"
              className="block text-xs font-bold text-slate-700 mb-1.5"
            >
              ชื่อผู้ตรวจสอบ (Inspector Name) <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <select
                id="inspector-name-select"
                required
                value={inspectorName}
                onChange={(e) => setInspectorName(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm font-semibold text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[48px] cursor-pointer shadow-xs"
              >
                {inspectors.length > 0 ? (
                  inspectors.map((ins) => (
                    <option key={ins.id} value={ins.name}>
                      {ins.name} {ins.role ? `(${ins.role})` : ''}
                    </option>
                  ))
                ) : (
                  <option value={inspectorName}>{inspectorName}</option>
                )}
              </select>
            </div>
          </div>

          <div>
            <label
              htmlFor="shift-select"
              className="block text-xs font-bold text-slate-700 mb-1.5"
            >
              กะการทำงาน (Shift)
            </label>
            <select
              id="shift-select"
              value={shift}
              onChange={(e) => setShift(e.target.value)}
              className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm font-semibold text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[48px] cursor-pointer shadow-xs"
            >
              <option value="กะเช้า (Day Shift)">กะเช้า (Day Shift)</option>
              <option value="กะบ่าย (Afternoon Shift)">กะบ่าย (Afternoon Shift)</option>
              <option value="กะดึก (Night Shift)">กะดึก (Night Shift)</option>
              <option value="กะพิเศษ (Overtime)">กะพิเศษ (Overtime)</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="running-hours-input"
              className="block text-xs font-bold text-slate-700 mb-1.5"
            >
              ชั่วโมงสะสมเครื่องขณะตรวจ (Operating Hours)
            </label>
            <div className="relative">
              <input
                id="running-hours-input"
                type="number"
                step="0.5"
                min="0"
                value={runningHours}
                onChange={(e) => setRunningHours(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm font-mono font-bold text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[48px] shadow-xs"
              />
              <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-semibold pointer-events-none">
                ชม.
              </span>
            </div>
          </div>
        </div>

        {/* Supervisor Remark / Corrective Actions */}
        <div>
          <label
            htmlFor="supervisor-notes-textarea"
            className="block text-xs font-bold text-slate-700 mb-1.5"
          >
            ข้อคิดเห็นหัวหน้างาน / การแก้ไขเบื้องต้น (Supervisor Notes):
          </label>
          <textarea
            id="supervisor-notes-textarea"
            rows={2}
            value={supervisorNotes}
            onChange={(e) => setSupervisorNotes(e.target.value)}
            placeholder="ระบุข้อคิดเห็น คำสั่งงาน หรือการแจ้งซ่อมเพิ่มเติม..."
            className="w-full bg-white border border-slate-300 rounded-lg p-3 text-xs text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none shadow-xs placeholder-slate-400"
          />
        </div>
      </div>

      {/* 5. HTML5 Canvas Signature Pad */}
      <SignaturePad
        signatureDataUrl={signatureDataUrl}
        onSaveSignature={(dataUrl) => setSignatureDataUrl(dataUrl)}
        onClearSignature={() => setSignatureDataUrl(undefined)}
        disabled={isSubmitting}
      />

      {/* Submit Error Message */}
      {submitError && (
        <div className="bg-rose-50 border border-rose-300 rounded-xl p-3.5 text-xs text-rose-800 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
          <span>{submitError}</span>
        </div>
      )}

      {/* 6. Big Touch Submit Action Button (>=48px) */}
      <button
        type="button"
        disabled={isSubmitting}
        onClick={() => handleSubmitInspection()}
        className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl text-base sm:text-lg font-black text-white shadow-xl transition-all min-h-[56px] select-none active:scale-[0.99] ${
          hasDefects
            ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-900/40 ring-2 ring-rose-400 animate-pulse'
            : 'bg-blue-600 hover:bg-blue-500 shadow-blue-900/40 ring-1 ring-blue-400'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isSubmitting ? (
          <>
            <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>กำลังบันทึกลงฐานข้อมูล IndexedDB...</span>
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            <span>
              {hasDefects
                ? 'บันทึกผลการตรวจสอบ PM (พบรายการ NG / แจ้งเตือนฉุกเฉิน)'
                : 'บันทึกผลการตรวจสอบ PM สมบูรณ์ (Save Inspection)'}
            </span>
          </>
        )}
      </button>

      {/* 7. Lubrication Guide Modal */}
      <LubricantGuideModal
        isOpen={isLubricantModalOpen}
        onClose={() => setIsLubricantModalOpen(false)}
        lubricants={template?.lubricants}
        safetyCautions={template?.safetyCautions}
      />
    </div>
  );
};
